import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {
  LexicalTypeaheadMenuPlugin,
  MenuOption,
  MenuTextMatch,
  useBasicTypeaheadTriggerMatch,
} from '@lexical/react/LexicalTypeaheadMenuPlugin';
import {TextNode} from 'lexical';
import {useCallback, useMemo, useState} from 'react';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { $createMentionNode } from '../nodes/MentionNode';
import { Avatar, List, ListItem, ListItemAvatar, ListItemButton, ListItemText, Paper } from '@mui/material';
import { stringAvatar } from '../../../utils/stringAvatar';
import type { WorkspaceUser } from '../../../types/workspaceUser';

// Define the MentionTypeaheadOption class
class MentionTypeaheadOption extends MenuOption {
  name: string;
  workspaceUser: WorkspaceUser;

  constructor(workspaceUser: WorkspaceUser) {
    super(workspaceUser.user.name);
    this.name = workspaceUser.user.name;
    this.workspaceUser = workspaceUser;
  }
}

// Define the MenuItem component
function MentionsTypeaheadMenuItem({
  index,
  isSelected,
  onClick,
  onMouseEnter,
  option,
}: {
  index: number;
  isSelected: boolean;
  onClick: () => void;
  onMouseEnter: () => void;
  option: MentionTypeaheadOption;
}) {
  const { user } = option.workspaceUser;
  
  return (
    <ListItem disablePadding>
      <ListItemButton
        selected={isSelected}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        sx={{
          '&.Mui-selected': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
          },
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
          },
        }}
      >
        <ListItemAvatar>
          <Avatar 
            src={user.picture} 
            {...(!user.picture && stringAvatar(user.name))} 
          />
        </ListItemAvatar>
        <ListItemText 
          primary={user.name}
          secondary={user.email}
          sx={{ 
            color: 'white',
            '.MuiListItemText-primary': {
              fontSize: '0.9rem',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            },
            '.MuiListItemText-secondary': {
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: '0.8rem',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }
          }}
        />
      </ListItemButton>
    </ListItem>
  );
}

const PUNCTUATION =
  '\\.,\\+\\*\\?\\$\\@\\|#{}\\(\\)\\^\\-\\[\\]\\\\/!%\'"~=<>_:;';
const NAME = '\\b[A-Z][^\\s' + PUNCTUATION + ']';

const DocumentMentionsRegex = {
  NAME,
  PUNCTUATION,
};

const PUNC = DocumentMentionsRegex.PUNCTUATION;

const TRIGGERS = ['@'].join('');

// Chars we expect to see in a mention (non-space, non-punctuation).
const VALID_CHARS = '[^' + TRIGGERS + PUNC + '\\s]';

// Non-standard series of chars. Each series must be preceded and followed by
// a valid char.
const VALID_JOINS =
  '(?:' +
  '\\.[ |$]|' + // E.g. "r. " in "Mr. Smith"
  ' |' + // E.g. " " in "Josh Duck"
  '[' +
  PUNC +
  ']|' + // E.g. "-' in "Salier-Hellendag"
  ')';

const LENGTH_LIMIT = 75;

const AtSignMentionsRegex = new RegExp(
  '(^|\\s|\\()(' +
    '[' +
    TRIGGERS +
    ']' +
    '((?:' +
    VALID_CHARS +
    VALID_JOINS +
    '){0,' +
    LENGTH_LIMIT +
    '})' +
    ')$',
);

function getPossibleQueryMatch(text: string): MenuTextMatch | null {
  const match = AtSignMentionsRegex.exec(text);
  if (match === null) {
    return null;
  }
  return {
    leadOffset: match.index,
    matchingString: match[3],
    replaceableString: match[2],
  };
}

// 50 is the longest alias length limit.
const ALIAS_LENGTH_LIMIT = 50;

// Regex used to match alias.
const AtSignMentionsRegexAliasRegex = new RegExp(
  '(^|\\s|\\()(' +
    '[' +
    TRIGGERS +
    ']' +
    '((?:' +
    VALID_CHARS +
    '){0,' +
    ALIAS_LENGTH_LIMIT +
    '})' +
    ')$',
);

// At most, 5 suggestions are shown in the popup.
const SUGGESTION_LIST_LENGTH_LIMIT = 5;

interface MentionsPluginProps {
  users: WorkspaceUser[];
}

export default function NewMentionsPlugin({ users }: MentionsPluginProps): JSX.Element | null {
  const [editor] = useLexicalComposerContext();

  const [queryString, setQueryString] = useState<string | null>(null);

  const results = useMemo(() => {
    if (!queryString) return users;
    const searchTerm = queryString.toLowerCase();
    return users.filter((workspaceUser) =>
      workspaceUser.user.name.toLowerCase().includes(searchTerm) ||
      workspaceUser.user.email.toLowerCase().includes(searchTerm)
    );
  }, [queryString, users]);

  const checkForSlashTriggerMatch = useBasicTypeaheadTriggerMatch('/', {
    minLength: 0,
  });

  const options = useMemo(
    () =>
      results
        .map((workspaceUser) => new MentionTypeaheadOption(workspaceUser))
        .slice(0, SUGGESTION_LIST_LENGTH_LIMIT),
    [results],
  );

  const onSelectOption = useCallback(
    (
      selectedOption: MentionTypeaheadOption,
      nodeToReplace: TextNode | null,
      closeMenu: () => void,
    ) => {
      editor.update(() => {
        const mentionNode = $createMentionNode({
          id: selectedOption.workspaceUser.user.id,
          name: selectedOption.workspaceUser.user.name,
          email: selectedOption.workspaceUser.user.email
        });
        if (nodeToReplace) {
          nodeToReplace.replace(mentionNode);
        }
        mentionNode.select();
        closeMenu();
      });
    },
    [editor],
  );

  const checkForMentionMatch = useCallback(
    (text: string) => {
      const slashMatch = checkForSlashTriggerMatch(text, editor);
      if (slashMatch !== null) {
        return null;
      }
      return getPossibleQueryMatch(text);
    },
    [checkForSlashTriggerMatch, editor],
  );

  return (
    <LexicalTypeaheadMenuPlugin<MentionTypeaheadOption>
      onQueryChange={setQueryString}
      onSelectOption={onSelectOption}
      triggerFn={checkForMentionMatch}
      options={options}
      menuRenderFn={(
        anchorElementRef,
        { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex },
      ) =>
        anchorElementRef.current && results.length
          ? ReactDOM.createPortal(
              <Paper
                elevation={3}
                sx={{
                  position: 'absolute',
                  left: '0',
                  bottom: '0',
                  transform: 'translateY(-56px)',
                  width: '250px',
                  maxHeight: '200px',
                  overflow: 'auto',
                  backgroundColor: '#313131',
                  border: '1px solid #414141',
                  zIndex: 10000,
                  marginBottom: '8px',
                  '&::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: '#212121',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: '#414141',
                    borderRadius: '4px',
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                    background: '#515151',
                  },
                }}
              >
                <List sx={{ 
                  padding: 0,
                  overflowX: 'hidden'
                }}>
                  {options.map((option, i: number) => (
                    <MentionsTypeaheadMenuItem
                      index={i}
                      isSelected={selectedIndex === i}
                      onClick={() => {
                        setHighlightedIndex(i);
                        selectOptionAndCleanUp(option);
                      }}
                      onMouseEnter={() => {
                        setHighlightedIndex(i);
                      }}
                      key={option.key}
                      option={option}
                    />
                  ))}
                </List>
              </Paper>,
              anchorElementRef.current,
            )
          : null
      }
    />
  );
}